import '@/App.css';
import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { FunnelProvider } from '@/context/FunnelContext';
import PublicShell from '@/components/PublicShell';
import Landing from '@/pages/Landing';
import FunnelStep from '@/pages/FunnelStep';
import ThankYou from '@/pages/ThankYou';
import { trackPageView } from '@/lib/analytics';
import MetaTracker from '@/components/MetaTracker';

// Everything below is lazily loaded so public visitors on mobile don't pay for
// the admin dashboard, creator portal, mockups, and legal-page code just to
// see the landing/funnel. This alone shaves ~100 KiB off the initial bundle.
const AdminLogin = lazy(() => import('@/pages/AdminLogin'));
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const LegalPage = lazy(() => import('@/pages/LegalPage'));
const Contact = lazy(() => import('@/pages/Contact'));
const MockupHome = lazy(() => import('@/pages/MockupHome'));
const MockupFunnel = lazy(() => import('@/pages/MockupFunnel'));
const MockupTabs = lazy(() => import('@/pages/MockupTabs'));
const PresellPA = lazy(() => import('@/pages/PresellPA'));
const PresellPaStart = lazy(() => import('@/pages/PresellPaStart'));
const PresellPasStart = lazy(() => import('@/pages/PresellPasStart'));
const PresellSPA = lazy(() => import('@/pages/PresellSPA'));
const AttorneyBio = lazy(() => import('@/pages/AttorneyBio'));
const CallTracking = lazy(() => import('@/pages/CallTracking'));
const AgentPerformance = lazy(() => import('@/pages/AgentPerformance'));
const IntakePerformance = lazy(() => import('@/pages/IntakePerformance'));
const PresellAdvertorial = lazy(() => import('@/pages/PresellAdvertorial'));
const PresellExperts = lazy(() => import('@/pages/PresellExperts'));
const PresellExpertsNavy = lazy(() => import('@/pages/PresellExpertsVariants').then(m => ({ default: m.PresellExpertsNavy })));
const PresellExpertsWarm = lazy(() => import('@/pages/PresellExpertsVariants').then(m => ({ default: m.PresellExpertsWarm })));
const PresellExpertsCorporate = lazy(() => import('@/pages/PresellExpertsVariants').then(m => ({ default: m.PresellExpertsCorporate })));
const TeamLandingPages = lazy(() => import('@/pages/TeamLanding'));
const SplitEntry = lazy(() => import('@/pages/SplitEntry'));
const CreatorPortal = lazy(() => import('@/pages/CreatorPortal'));
const PublicRetainedReport = lazy(() => import('@/pages/PublicRetainedReport'));
const MockupCtaIndex = lazy(() => import('@/pages/MockupCtaIndex'));
const MockupCtaVariant = lazy(() => import('@/pages/MockupCtaVariant'));

// Wrap each lazy route in Suspense with a bare-bones fallback (blank screen)
// — no spinners on the critical path.
const L = (el) => <Suspense fallback={null}>{el}</Suspense>;

// Sends a GA4 page_view on every client-side route change (SPA tracking).
function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
  return null;
}

// Team-landing bundles TeamOverlay + TeamSplit; small wrapper reads the right
// export per route while keeping both in one chunk.
function TeamRoute({ variant }) {
  return (
    <Suspense fallback={null}>
      <TeamLandingPages variant={variant} />
    </Suspense>
  );
}

function App() {
  return (
    <FunnelProvider>
      <BrowserRouter>
        <AnalyticsTracker />
        <MetaTracker />
        <Routes>
          {/* Public funnel pages share a persistent shell (fixed houses + tiny footer). */}
          <Route element={<PublicShell />}>
            <Route path="/" element={<Landing />} />
            <Route path="/sp" element={<Landing sourcePage="sp" pageLang="es" />} />
            <Route path="/flow/:step" element={<FunnelStep />} />
            <Route path="/thank-you" element={<ThankYou />} />
          </Route>

          {/* Standalone pages keep their own simple scrollable layout. */}
          <Route path="/mockup" element={L(<MockupHome />)} />
          <Route path="/mockup/funnel" element={L(<MockupFunnel />)} />
          <Route path="/mockup/tabs" element={L(<MockupTabs />)} />
          <Route path="/mockup/cta" element={L(<MockupCtaIndex />)} />
          <Route path="/mockup/cta-1" element={L(<MockupCtaVariant variant={1} />)} />
          <Route path="/mockup/cta-2" element={L(<MockupCtaVariant variant={2} />)} />
          <Route path="/mockup/cta-3" element={L(<MockupCtaVariant variant={3} />)} />
          <Route path="/mockup/cta-4" element={L(<MockupCtaVariant variant={4} />)} />
          <Route path="/mockup/cta-5" element={L(<MockupCtaVariant variant={5} />)} />
          <Route path="/pa" element={L(<PresellPA
            phone="(833) 734-4655"
            phoneHref="tel:+18337344655"
            pageMeta={{
              title: 'California Lemon Law Help | Free 60-Second Case Review — The Lemon Pros',
              description: "Think your car is a lemon? California lemon law may entitle you to a refund, replacement, or cash. Free 60-second case review. No win, no fee.",
              canonical: '/pa',
            }} />)} />
          {/* Direct-to-funnel entry (skips the /pa advertorial). Back button
              returns to /pa, phone + thank-you fall back to the English
              ChatGPT line. See PresellPaStart.jsx. */}
          <Route path="/pa-start" element={L(<PresellPaStart />)} />
          {/* Spanish version — skips /spa advertorial, Back returns to /spa,
              phone falls back to the Spanish ChatGPT line. */}
          <Route path="/pas-start" element={L(<PresellPasStart />)} />
          <Route path="/pa-experts" element={L(<PresellExpertsWarm />)} />
          {/* Preserve any Google Ads clicks or bookmarks aimed at the older
              /lemon-law-experts slug — permanent redirect to /pa-experts,
              query string preserved. */}
          <Route path="/lemon-law-experts" element={<Navigate to="/pa-experts" replace />} />
          <Route path="/experts-a" element={L(<PresellExpertsNavy />)} />
          <Route path="/experts-b" element={L(<PresellExpertsWarm />)} />
          <Route path="/experts-c" element={L(<PresellExpertsCorporate />)} />
          {/* Individual attorney bio pages for E-E-A-T + AI citations. */}
          <Route path="/attorneys/:slug" element={L(<AttorneyBio />)} />
          <Route path="/pa-mockup" element={L(<PresellAdvertorial sourcePage="lapa" />)} />
          <Route
            path="/pa-mockup-text"
            element={L(
              <PresellPA
                textPhone="(844) 335-8911"
                textPhoneHref="sms:+18443358911"
                textMessage="Hi, I'd like to check if my car qualifies for California Lemon Law."
                rootTestId="presell-pa-text-page"
              />,
            )}
          />
          <Route path="/spa" element={L(<PresellSPA
            phone="(877) 265-1141"
            phoneHref="tel:+18772651141"
          />)} />
          <Route
            path="/pad"
            element={L(
              <PresellPA
                sourcePage="lapad"
                phone="(844) 618-1036"
                phoneHref="tel:+18446181036"
                callConversionLabel="AW-318021992/B06_CIvKyNUcEOjC0pcB"
                rootTestId="presell-pad-page"
              />,
            )}
          />
          <Route path="/tm" element={<TeamRoute variant="overlay" />} />
          <Route path="/tm2" element={<TeamRoute variant="split" />} />
          <Route
            path="/dg"
            element={L(<PresellPA contentPath="/dg-content" sourcePage="ladg" phone="(833) 240-9312" phoneHref="tel:+18332409312" callConversionLabel="AW-318021992/ZfC7CPveytUcEOjC0pcB" rootTestId="presell-dg-page" />)}
          />
          <Route
            path="/dgs"
            element={L(<PresellSPA contentPath="/dgs-content" sourcePage="ladgs" phone="(833) 868-1802" phoneHref="tel:+18338681802" callConversionLabel="AW-318021992/2AGnCKeiydUcEOjC0pcB" rootTestId="presell-dgs-page" />)}
          />
          <Route path="/split" element={L(<SplitEntry />)} />
          <Route path="/contact" element={L(<Contact />)} />
          <Route path="/terms" element={L(<LegalPage />)} />
          <Route path="/privacy" element={L(<LegalPage />)} />
          <Route path="/do-not-sell" element={L(<LegalPage />)} />
          <Route path="/admin" element={L(<AdminLogin />)} />
          <Route path="/admin/dashboard" element={L(<AdminDashboard />)} />
          <Route path="/call-tracking" element={L(<CallTracking />)} />
          <Route path="/call-tracking/agent/:ext" element={L(<AgentPerformance />)} />
          <Route path="/call-tracking/intake/:ext" element={L(<IntakePerformance />)} />
          <Route path="/creator-portal" element={L(<CreatorPortal />)} />
          {/* Public retained-clients report — aggregate only, no PII */}
          <Route path="/reports/retained" element={L(<PublicRetainedReport />)} />
          {/* Split-test entry slugs: /split, /split2, /split3, or custom. Must stay
              last so all explicit routes above take precedence. */}
          <Route path="/:splitSlug" element={L(<SplitEntry />)} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </FunnelProvider>
  );
}

export default App;
